import type { BoardStage } from 'chery-contracts';
import { cn } from '../../lib/cn';
import { MATRIX, MATRIX_CAPTION, MATRIX_COLUMNS, MATRIX_HINT, MATRIX_LEGEND, MATRIX_NOTE, MATRIX_TITLE, type MatrixLevel } from './content/divisions';

const Dot = ({ level }: { level: MatrixLevel }) => (
  <span
    aria-label={level === 2 ? 'direct' : level === 1 ? 'indirect' : 'none'}
    className={cn(
      'inline-block size-3 rounded-full',
      level === 2 ? 'bg-primary' : level === 1 ? 'border-[1.5px] border-primary' : 'border border-border',
    )}
  />
);

export interface AccountabilityMatrixProps {
  selected: BoardStage;
  onSelect: (stage: BoardStage) => void;
}

/** Fig. 3 — which division moves which board number; a row opens its stage. */
export const AccountabilityMatrix = ({ selected, onSelect }: AccountabilityMatrixProps) => (
  <section
    className="space-y-3 rounded-lg border border-border bg-card p-5"
    data-accountability-matrix
  >
    <div>
      <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{MATRIX_CAPTION}</p>
      <h2 className="text-lg font-semibold text-foreground">{MATRIX_TITLE}</h2>
      <p className="text-xs text-muted-foreground">{MATRIX_HINT}</p>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[36rem] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border">
            <th />
            {MATRIX_COLUMNS.map((column) => (
              <th
                key={column}
                scope="col"
                className="px-2 py-2 text-center text-xs font-medium uppercase tracking-wide text-muted-foreground"
              >
                {column}
              </th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {MATRIX.map((row) => (
            <tr
              key={row.code}
              className={cn('cursor-pointer border-b border-border last:border-b-0 hover:bg-muted/60', selected === row.stage && 'bg-muted')}
              onClick={() => onSelect(row.stage)}
              data-matrix-row={row.code}
            >
              <th
                scope="row"
                className="px-2 py-2 text-start font-normal"
              >
                <span className="me-2 inline-block w-14 font-mono text-xs text-muted-foreground">{row.code}</span>
                {row.who}
              </th>
              {row.cells.map((level, index) => (
                <td
                  key={MATRIX_COLUMNS[index]}
                  className="px-2 py-2 text-center"
                >
                  <Dot level={level} />
                </td>
              ))}
              <td className="px-2 py-2 text-end">
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={(event) => {
                    event.stopPropagation();
                    onSelect(row.stage);
                  }}
                >
                  {row.stageLabel} →
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <Dot level={2} />
        {MATRIX_LEGEND.direct}
      </span>
      <span className="flex items-center gap-1.5">
        <Dot level={1} />
        {MATRIX_LEGEND.indirect}
      </span>
    </div>
    <p className="max-w-4xl text-xs leading-relaxed text-muted-foreground">{MATRIX_NOTE}</p>
  </section>
);
